import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

const SINTERED_DUTCH_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/clean-macro-photograph-of-sintered-dutch-a162a4.png";
const MULTILAYER_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Multi-Layer-Sintered-Woven-Wire-Mesh-Discs.jpg";
const CUSTOM_SINTERED_IMAGE =
  "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1775455/img/professional-photograph-of-multilayer-si-406f25.png";
const SINTERED_PARENT_IMAGE =
  "https://mutualfilter.com/wp-content/uploads/2026/09/Sintered-Mesh-Filter-Elements-of-alloyfilter.jpg?v=hy0enjdkewc";

export const COPY: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintered Wire Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Sintered Dutch Weave Mesh",
      lead: "Engineered diffusion-bonded laminates combining fine Dutch weave filter cloth with robust backing and protective mesh layers. Designed for precision surface filtration, high differential pressure resistance, and dependable backwash regeneration.",
      secondaryCta: {
        label: "View Sintered Wire Mesh",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Sintered Dutch weave wire mesh laminate cross-section and surface",
      },
      cardNote: "Solid-state diffusion bonded",
      cardLink: {
        label: "Technical Overview",
        to: "#overview",
      },
      specs: [
        { label: "Filtration Rating", value: "1 µm – 200 µm" },
        { label: "Standard Alloys", value: "AISI 316L / 304L" },
        { label: "Construction", value: "Dutch Core + Protective Laminate" },
        { label: "Standard Forms", value: "Sheets, Discs & Tubes" },
      ],
    },
    overview: {
      eyebrow: "Engineering & Structure",
      heading: "Monolithic Dutch Weave Filter Laminates",
      paragraphs: [
        "Sintered Dutch weave mesh integrates dense Dutch weave wire cloth with coarse square mesh layers into a single rigid laminate through high-temperature vacuum diffusion bonding. The solid-state bonding process fuses every wire intersection at the atomic level without solders or chemical binding agents, ensuring zero particle shed and permanent pore geometry.",
        "While conventional Dutch weave wire cloth offers precise aperture ratings down to the single-micron scale, high working differential pressures can deform loose wires and shift pore distributions. By diffusion-bonding protective mesh on the upstream side and heavy structural reinforcement layers downstream, the sintered composite retains its aperture size under extreme forward flow and cyclic reverse cleaning.",
        "The resulting metallic matrix behaves as an incompressible surface filtration medium. Captured solids form a structured surface filter cake that can be dislodged repeatedly via pulse backwashing, liquid flush, ultrasonic baths, or chemical solvents without structural delamination.",
      ],
      keyPoints: [
        "Solid-state vacuum diffusion bonding locks all wire contact points permanently, eliminating media migration and pore deflection.",
        "High mechanical rigidity withstands severe differential pressure spikes, cyclic pulse cleaning, and mechanical vibration.",
        "Regenerable surface filtration structure suited for ultrasonic cleaning, backwashing, solvent wash, and thermal burnout.",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Macro photograph of sintered Dutch weave stainless steel mesh showing precision surface pores",
      },
    },
    specTable: {
      heading: "Standard Sintered Dutch Weave Specifications",
      intro: "Representative diffusion-bonded Dutch weave specifications across fine and intermediate filtration ratings. Custom configurations and alternative layer stackups are engineered upon request.",
      columns: [
        { key: "mesh", label: "Core Dutch Weave" },
        { key: "rating", label: "Nominal Rating (µm)" },
        { key: "layers", label: "Layer Stackup" },
        { key: "thickness", label: "Nominal Thickness" },
        { key: "alloys", label: "Standard Material" },
      ],
      rows: [
        {
          mesh: "Plain Dutch 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 to 4 Layers (Dutch + Support + Skeleton)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Twill Dutch 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 Layers (Protective + Dutch + Buffer + Support)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Twill Dutch 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 to 5 Layers (Protective + Dutch + Reinforced)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Twill Dutch 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 Layers (Protective + Dutch + Buffer + Double Skeleton)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "Twill Dutch 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 Layers (Protective + Dutch + Triple Support)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "Values represent standard configurations diffusion-bonded from ASTM E2016 / ASTM E2814 wire cloth. Custom alloy combinations and thickness profiles are available on request.",
    },
    features: {
      heading: "Key Performance Features",
      intro: "Designed specifically to overcome the structural limits of unbonded wire cloth in critical industrial filtration processes.",
      items: [
        {
          title: "Fixed Aperture Stability",
          text: "Vacuum diffusion bonding secures fine shute and warp wires in place, guaranteeing consistent micron retention without aperture widening under continuous pressure.",
        },
        {
          title: "Zero Media Migration",
          text: "Because bonding occurs without metallic solders or polymer binders, no loose wire segments, adhesives, or particulates shed into the downstream filtrate.",
        },
        {
          title: "High Differential Pressure",
          text: "Integrated coarse backing layers distribute mechanical loads across the laminate, preventing pleat collapse, blowout, or structural deformation during pressure surges.",
        },
        {
          title: "Complete Cleanability",
          text: "The hard, metallic surface enables repeated backwashing, automated blowback, high-temperature solvent immersion, and ultrasonic regeneration with full recovery.",
        },
      ],
    },
    related: {
      heading: "Related Sintered Mesh Solutions",
      intro: "Explore complementary diffusion-bonded wire mesh configurations and custom fabrication capabilities.",
      cards: [
        {
          title: "Sintered Multilayer Mesh",
          text: "Standard 3-layer, 5-layer, and multi-layer square mesh diffusion-bonded laminates for fluidization, gas filtration, and general liquid processing.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "Multi-layer sintered wire mesh discs and sheets",
          },
          badge: "3-Layer & 5-Layer",
          linkLabel: "View Multilayer Mesh",
        },
        {
          title: "Custom Sintered Mesh",
          text: "Tailored laminations from 2 to 9+ composite layers, combined with perforated metal backing plates and specialized form factors.",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "Custom engineered multi-layer sintered stainless steel laminate",
          },
          badge: "Bespoke Laminates",
          linkLabel: "View Custom Sintered",
        },
        {
          title: "Sintered Wire Mesh Hub",
          text: "Browse our complete range of diffusion-bonded porous metallic media, engineering guides, and industrial application solutions.",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "Sintered wire mesh filter elements overview",
          },
          badge: "Product Family",
          linkLabel: "View All Sintered Mesh",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "Custom Engineering & Supply",
      heading: "Request a Sintered Dutch Weave Specification",
      lead: "Provide your required micron rating, alloy grade, operating differential pressure, and finished component dimensions for a technical evaluation and formal quotation.",
      secondaryLabel: "Browse Sintered Wire Mesh",
      secondaryTo: "/SinteredWireMeshPage",
      note: "Available in sheets, slit coils, laser-cut discs, seam-welded tubes, and custom fabricated filter assemblies.",
    },
  },
  zh: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "烧结不锈钢丝网",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "荷兰编织烧结丝网",
      lead: "工程级真空固相扩散烧结复合滤材，将精密荷兰密纹网与高强度保护网及多层重型支撑骨架融为一体。专为高精度表面截留、严苛工作压差及反复反洗再生工况设计。",
      secondaryCta: {
        label: "浏览烧结丝网全系列",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "荷兰编织烧结丝网复合截面与微观表面",
      },
      cardNote: "高温真空固相扩散烧结",
      cardLink: {
        label: "技术结构概述",
        to: "#overview",
      },
      specs: [
        { label: "过滤精度", value: "1 µm – 200 µm" },
        { label: "标准材质", value: "AISI 316L / 304L" },
        { label: "结构形式", value: "荷兰核心层 + 保护支撑复合" },
        { label: "供应形式", value: "整板、滤片及焊接滤筒" },
      ],
    },
    overview: {
      eyebrow: "工艺与微观结构",
      heading: "整体式荷兰密纹烧结复合板",
      paragraphs: [
        "荷兰编织烧结网在严格控制的高温真空炉内，将精密荷兰编织金属滤网（平纹或斜纹密纹网）与保护层、分流缓冲层及粗目支撑骨架网经固相扩散烧结工艺紧密结合。在原子级扩散结合作用下，所有交织金属丝节点牢固熔合，无需任何焊料或化学粘合剂，杜绝介质脱落与纤维迁移。",
        "传统未烧结荷兰编织丝网虽具备微米级精密孔径，但在高操作压差或脉冲冲击下易发生微丝位移和孔隙变形。荷兰烧结网通过在上游复合保护网、下游复合重型支撑骨架，使精密滤层在正向高压过滤与周期性反洗循环中保持绝对稳定的孔径分布与几何尺寸。",
        "整体成型的金属多孔板表现为完全刚性的表面过滤介质。被截留的颗粒杂质在滤材外表面形成规则的滤饼层，可通过在线脉冲反吹、液体反洗、超声波清洗或化学溶剂浸洗彻底清除，结构不会分层脱焊。",
      ],
      keyPoints: [
        "高温真空固相扩散烧结使所有金属丝节点实现冶金级熔合，消除介质脱落与网孔移位风险。",
        "高机械结构强度与抗压刚度，耐受严苛工作压差尖峰、脉冲反洗冲击及机械振动。",
        "刚性表面截留结构支持超声波清洗、溶剂浸洗、水汽反洗及高温热解再生，使用寿命持久。",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "荷兰编织烧结不锈钢丝网微距特写展示精密表面孔隙",
      },
    },
    specTable: {
      heading: "荷兰编织烧结丝网典型规格",
      intro: "展示荷兰编织烧结复合网常用核心滤层、精度等级及复合厚度参数。可根据客户流体工艺条件定制层级配置。",
      columns: [
        { key: "mesh", label: "核心荷兰网规格" },
        { key: "rating", label: "公称精度 (µm)" },
        { key: "layers", label: "复合层数与结构" },
        { key: "thickness", label: "公称厚度" },
        { key: "alloys", label: "常用材质" },
      ],
      rows: [
        {
          mesh: "平纹荷兰 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 至 4 层复合 (密纹层 + 缓冲层 + 骨架层)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "斜纹荷兰 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 层复合 (保护层 + 密纹层 + 缓冲层 + 支撑层)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "斜纹荷兰 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 至 5 层复合 (保护层 + 密纹层 + 加强支撑)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "斜纹荷兰 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 层复合 (保护层 + 密纹层 + 分流层 + 双骨架)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "斜纹荷兰 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 层复合 (保护层 + 密纹层 + 三重支撑骨架)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "数据基于 ASTM E2016 及 ASTM E2814 标准不锈钢编织丝网扩散烧结制造，特殊厚度与层叠组合可按需定制。",
    },
    features: {
      heading: "核心性能优势",
      intro: "专为解决传统编织金属丝网在工业苛刻过滤工况中易变形、易脱落与承压受限等痛点而设计。",
      items: [
        {
          title: "恒定稳定的孔径结构",
          text: "真空固相扩散结合牢固锁死纬丝与经丝交叉点，在持续高压差冲刷下孔径绝无形变或扩张，确保过滤截留精度始终如一。",
        },
        {
          title: "零介质迁移风险",
          text: "固相热熔接合无任何焊料、粘接树脂或化学添加剂，下游滤液绝不受到金属脱丝、碎屑或粘合剂溶出污染。",
        },
        {
          title: "卓越机械抗压性能",
          text: "集成高目数粗丝支撑骨架层，有效分散流体剪切力与工作压差，避免滤材在高负荷工况下发生凹陷、褶皱变形或破损。",
        },
        {
          title: "全方位再生清洗性能",
          text: "刚性平整的金属表面易于形成滤饼剥离，全面支持在线脉冲反冲洗、溶剂浸泡、高温煅烧脱脂与超声波深度再生。",
        },
      ],
    },
    related: {
      heading: "相关烧结金属滤材",
      intro: "查看互拓提供的多样化扩散烧结多孔材料结构与定制深加工解决方案。",
      cards: [
        {
          title: "多层烧结丝网",
          text: "标准 3 层、5 层及多层方孔金属丝网复合结构，广泛用于流化床分布板、气体除尘脱水及通用固液分离系统。",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "多层烧结不锈钢丝网滤片与滤板",
          },
          badge: "3层与5层标准结构",
          linkLabel: "查看多层烧结网",
        },
        {
          title: "定制烧结网结构与组件",
          text: "支持 2 至 9+ 层非标复合、结合冲孔金属基板加强以及定制激光裁切、折波及精密缝焊滤芯组件。",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "定制多层复合烧结金属丝网结构板",
          },
          badge: "非标定制层级",
          linkLabel: "查看定制烧结网",
        },
        {
          title: "烧结丝网产品中心",
          text: "浏览互拓全系列真空扩散烧结滤材、选型技术指南、材质牌号对比及各工业领域典型应用方案。",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "烧结不锈钢滤网滤芯组件全景",
          },
          badge: "产品大类",
          linkLabel: "浏览烧结丝网",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "技术咨询与工程定制",
      heading: "获取荷兰编织烧结网规格方案与报价",
      lead: "请提供您的目标过滤精度、不锈钢材质要求、工作压差条件及成品零件尺寸，互拓工程团队将为您提供专业选型建议与正式报价单。",
      secondaryLabel: "返回烧结网总览",
      secondaryTo: "/SinteredWireMeshPage",
      note: "可按订单提供大规格标准板、精密激光冲片、分条卷带、缝焊滤筒及异形复合滤芯组件。",
    },
  },
  es: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malla metálica sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla holandesa sinterizada",
      lead: "Laminados multicapa unidos por difusión al vacío que combinan tela metálica holandesa de alta precisión con capas protectoras y mallas de refuerzo rígidas. Diseñada para retención superficial micrométrica, alta resistencia mecánica y retrolavado continuo.",
      secondaryCta: {
        label: "Ver malla sinterizada",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Corte transversal y superficie de malla holandesa sinterizada",
      },
      cardNote: "Unión por difusión en estado sólido",
      cardLink: {
        label: "Descripción técnica",
        to: "#overview",
      },
      specs: [
        { label: "Grado de filtración", value: "1 µm – 200 µm" },
        { label: "Aleaciones estándar", value: "AISI 316L / 304L" },
        { label: "Construcción", value: "Núcleo holandés + refuerzo" },
        { label: "Formatos", value: "Láminas, discos y tubos" },
      ],
    },
    overview: {
      eyebrow: "Estructura e ingeniería",
      heading: "Laminados monolíticos de tejido holandés sinterizado",
      paragraphs: [
        "La malla holandesa sinterizada integra tela metálica de tejido holandés denso con capas de malla cuadrada gruesa en un único laminado rígido mediante difusión térmica al vacío. Este proceso de estado sólido fusiona atómicamente cada cruce de alambre sin soldaduras ni adhesivos químicos, garantizando ausencia de desprendimiento y una geometría de poros permanente.",
        "Aunque la tela de tejido holandés convencional proporciona aberturas precisas en escala micrométrica, las presiones diferenciales elevadas pueden desplazar los alambres libres y alterar la distribución de poros. Al sinterizar capas protectoras aguas arriba y refuerzos estructurales aguas abajo, el laminado conserva su calibración ante flujos intensos y ciclos de limpieza inversa.",
        "La estructura metálica monolítica actúa como un medio de filtración superficial incompresible. Los sólidos retenidos forman una torta superficial uniforme que se desprende con facilidad mediante pulsos de retrolavado, ultrasonidos, lavado químico o desengrase térmico sin riesgo de deslaminación.",
      ],
      keyPoints: [
        "La unión por difusión al vacío en estado sólido fija permanentemente todos los cruces, eliminando la migración de fibras y la deformación de poros.",
        "Elevada rigidez mecánica para resistir sobrepresiones diferenciales continuas, pulsos de retrolavado y vibraciones del proceso.",
        "Estructura filtrante de superficie regenerable mediante limpieza por ultrasonidos, disolventes químicos y ciclos térmicos.",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Fotografía macro de malla holandesa sinterizada de acero inoxidable",
      },
    },
    specTable: {
      heading: "Especificaciones habituales de malla holandesa sinterizada",
      intro: "Configuraciones representativas unidas por difusión en rangos de filtración finos e intermedios. Posibilidad de desarrollar laminaciones especiales.",
      columns: [
        { key: "mesh", label: "Tejido holandés núcleo" },
        { key: "rating", label: "Retención nominal (µm)" },
        { key: "layers", label: "Estructura de capas" },
        { key: "thickness", label: "Espesor nominal" },
        { key: "alloys", label: "Aleación estándar" },
      ],
      rows: [
        {
          mesh: "Holandés liso 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 a 4 capas (núcleo + buffer + soporte)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandés cruzado 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 capas (protección + núcleo + amortiguador + soporte)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandés cruzado 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 a 5 capas (protección + núcleo + refuerzo)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandés cruzado 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 capas (protección + núcleo + buffer + doble refuerzo)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "Holandés cruzado 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 capas (protección + núcleo + triple soporte)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "Datos calculados para telas conformes a ASTM E2016 y ASTM E2814 unidas por difusión. Configuraciones personalizadas bajo pedido.",
    },
    features: {
      heading: "Características técnicas principales",
      intro: "Diseñada para eliminar los puntos débiles de la tela metálica sin sinterizar en aplicaciones industriales severas.",
      items: [
        {
          title: "Estabilidad absoluta de poro",
          text: "La unión por difusión inmoviliza los alambres de trama y urdimbre, evitando la dilatación o ensanchamiento de los poros bajo presiones sostenidas.",
        },
        {
          title: "Cero migración de material",
          text: "Al no emplear soldaduras de aporte ni resinas aglutinantes, no existe riesgo de desprendimiento de fibras metálicas ni contaminación química del filtrado.",
        },
        {
          title: "Resistencia a alta presión",
          text: "Las capas de refuerzo integradas absorben las cargas hidrodinámicas, evitando colapsos estructurales o roturas en caso de picos de presión.",
        },
        {
          title: "Excelente capacidad de regeneración",
          text: "La superficie lisa y rígida facilita el desprendimiento de sólidos y permite retrolavado intensivo, baños de ultrasonidos y regeneración térmica completa.",
        },
      ],
    },
    related: {
      heading: "Soluciones complementarias de malla sinterizada",
      intro: "Conozca otras configuraciones sinterizadas por difusión y opciones de fabricación especial.",
      cards: [
        {
          title: "Malla multicapa sinterizada",
          text: "Estructuras estándar de 3 y 5 capas de malla cuadrada para fluidización, filtración de gases y clarificación de líquidos.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "Discos y láminas de malla metálica multicapa sinterizada",
          },
          badge: "3 y 5 capas",
          linkLabel: "Ver malla multicapa",
        },
        {
          title: "Malla sinterizada a medida",
          text: "Laminados personalizados de 2 a 9+ capas, con lámina perforada de refuerzo y elementos conformados.",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "Laminado de malla sinterizada a medida",
          },
          badge: "Estructuras a medida",
          linkLabel: "Ver malla a medida",
        },
        {
          title: "Gama completa de malla sinterizada",
          text: "Explore el catálogo general de medios metálicos porosos sinterizados, tablas de compatibilidad y aplicaciones.",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "Elementos filtrantes de malla sinterizada",
          },
          badge: "Familia de productos",
          linkLabel: "Ver todo en sinterizados",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "Suministro e ingeniería",
      heading: "Solicite una cotización de malla holandesa sinterizada",
      lead: "Envíenos su micraje requerido, aleación deseada, presión diferencial estimada y dimensiones finales para recibir una propuesta técnica y comercial.",
      secondaryLabel: "Volver a malla sinterizada",
      secondaryTo: "/SinteredWireMeshPage",
      note: "Disponible en placas estándar, discos cortados por láser, tiras continuas y cartuchos cilíndricos soldados.",
    },
  },
  de: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Sintermetallgewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Gesintertes Tressengewebe",
      lead: "Diffusionsgebundene Mehrlagenverbunde aus feinem Tressengewebe und robusten Stütz- sowie Schutzgewebelagen. Entwickelt für präzisen Oberflächenrückhalt, hohe Differenzdruckstabilität und wiederholte Rückspülbarkeit.",
      secondaryCta: {
        label: "Übersicht Sintergewebe",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Querschnitt und Oberfläche von gesintertem Tressengewebe",
      },
      cardNote: "Vakuum-Diffusionsschweißen",
      cardLink: {
        label: "Technischer Überblick",
        to: "#overview",
      },
      specs: [
        { label: "Filterfeinheit", value: "1 µm – 200 µm" },
        { label: "Standardlegierungen", value: "AISI 316L / 304L" },
        { label: "Verbundstruktur", value: "Tressenkern + Stützlagen" },
        { label: "Lieferformen", value: "Platten, Ronden & Filterrohre" },
      ],
    },
    overview: {
      eyebrow: "Konstruktion und Schichtaufbau",
      heading: "Monolithische Sinterverbunde aus Tressengewebe",
      paragraphs: [
        "Gesintertes Tressengewebe verbindet dicht gewebte Tressengewebe mit groben Quadratmaschengeweben im Hochvakuum-Diffusionsverfahren zu einem monolithischen, formstabilen Filterlaminat. Die Festkörper-Diffusion verschweißt jeden Drahtkreuzungspunkt auf atomarer Ebene ohne Lotmittel oder Klebharze.",
        "Während konventionelle Drahtgewebe im Tressengeflecht zwar feine Abscheidegrade im Mikrometerbereich bieten, neigen ungesinterte Drähte unter Druckstößen zum Wandern und Aufweiten. Durch das Diffusionssintern mit vorgeschalteter Schutzlage und nachgeschaltetem Stützskelett bleibt die Porengeometrie auch bei hohen Strömungsgeschwindigkeiten und pulsierender Gegenstromspülung formtreu.",
        "Das fertige Laminat agiert als inkompressibles Oberflächenfiltermedium. Abgeschiedene Feststoffe lagern sich auf der glatten Oberfläche ab und können durch Rückspülung, Ultraschallbäder, Lösungsmittel oder thermische Reinigung vollständig abgetragen werden, ohne dass Schichtablösungen auftreten.",
      ],
      keyPoints: [
        "Festkörper-Diffusionsschweißen arretiert alle Drahtkreuzungen dauerhaft ohne Medienmigration oder Porenverzug.",
        "Hohe mechanische Druckfestigkeit gegenüber Betriebsüberdrücken, Druckstößen und mechanischen Schwingungen.",
        "Vollständig regenerierbare Oberflächenfiltermatrix für Ultraschallbad, Rückspülung und thermische Pyrolyse.",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Makroaufnahme von gesintertem Edelstahl-Tressengewebe mit feinen Porenöffnungen",
      },
    },
    specTable: {
      heading: "Typische Spezifikationen für gesintertes Tressengewebe",
      intro: "Repräsentative Ausführungen im feinen und mittleren Filtrationsbereich. Kundenspezifische Schichtkombinationen fertigen wir auf Anfrage.",
      columns: [
        { key: "mesh", label: "Tressengewebe-Kern" },
        { key: "rating", label: "Nennfeinheit (µm)" },
        { key: "layers", label: "Schichtaufbau" },
        { key: "thickness", label: "Nenndicke" },
        { key: "alloys", label: "Standardwerkstoff" },
      ],
      rows: [
        {
          mesh: "Glatte Tresse 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 bis 4 Lagen (Kern + Puffer + Stützgewebe)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Köpertresse 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 Lagen (Schutzlage + Tresse + Puffer + Stützlage)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Köpertresse 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 bis 5 Lagen (Schutzlage + Tresse + Stützung)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Köpertresse 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 Lagen (Schutz + Tresse + Puffer + Doppelskelett)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "Köpertresse 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 Lagen (Schutz + Tresse + Dreifachstütze)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "Basisdaten nach ASTM E2016 und ASTM E2814 gefertigter Drahtgewebeverbunde. Sonderausführungen auf Anfrage.",
    },
    features: {
      heading: "Leistungsmerkmale",
      intro: "Entwickelt zur Beseitigung mechanischer Schwachstellen ungesinterter Gewebe in anspruchsvollen Prozessumgebungen.",
      items: [
        {
          title: "Dauerhafte Porenstabilität",
          text: "Das Diffusionssintern fixiert Schuss- und Kettfäden zuverlässig, sodass auch unter kontinuierlicher Last keine Porenweitenveränderungen auftreten.",
        },
        {
          title: "Keine Partikelemission",
          text: "Da der Verbund frei von Loten oder Harzen ist, gelangen weder Drahtfragmente noch gelöste Fremdstoffe in das nachgeschaltete Filtrat.",
        },
        {
          title: "Hohe Differenzdruckfestigkeit",
          text: "Integrierte Stützdrähte leiten Strömungskräfte homogen in die Struktur ab und verhindern Plissierungskollaps oder Durchblasen.",
        },
        {
          title: "Exzellente Regenerierbarkeit",
          text: "Die glatte metallische Oberfläche begünstigt ein rasches Abreinigen des Filterkuchens mittels Gegenstromspülung oder Ultraschall.",
        },
      ],
    },
    related: {
      heading: "Ergänzende Sintermetall-Lösungen",
      intro: "Entdecken Sie weitere diffusionsgebundene Drahtgewebestrukturen und maßgefertigte Filterelemente.",
      cards: [
        {
          title: "Mehrlagen-Sinterdrahtgewebe",
          text: "Klassische 3-Lagen-, 5-Lagen- und verstärkte Quadratmaschenlaminate für Wirbelschichtböden, Gasreinigung und Flüssigkeitsfiltration.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "Mehrlagige gesinterte Drahtgewebelaminate und Ronden",
          },
          badge: "3-Lagen & 5-Lagen",
          linkLabel: "Mehrlagengewebe anzeigen",
        },
        {
          title: "Kundenspezifisches Sintergewebe",
          text: "Sonderaufbauten von 2 bis 9+ Verbundlagen, Kombinationen mit Lochblechen und kundenspezifische Rondenfertigung.",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "Kundenspezifisch gesintertes Mehrlagenlaminat",
          },
          badge: "Sonderaufbauten",
          linkLabel: "Sondergewebe anzeigen",
        },
        {
          title: "Produktübersicht Sintergewebe",
          text: "Gesamtes Sortiment diffusionsgebundener poröser Metallmedien, Auslegungshilfen und Anwendungsberichte.",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "Edelstahl-Sintergewebe Filterelemente",
          },
          badge: "Produktfamilie",
          linkLabel: "Alle Sintergewebe",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "Engineering & Fertigung",
      heading: "Spezifikation für gesintertes Tressengewebe anfragen",
      lead: "Nennen Sie uns Filterfeinheit, Werkstoff, Differenzdruckanforderungen und Elementabmessungen für eine fundierte technische Auslegung und Angebotserstellung.",
      secondaryLabel: "Zurück zu Sintergewebe",
      secondaryTo: "/SinteredWireMeshPage",
      note: "Lieferbar als Plattenware, lasergeschnittene Ronden, Spaltbänder, nahtgeschweißte Filterkerzen und Formteile.",
    },
  },
  fr: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Toile métallique frittée",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile hollandaise frittée",
      lead: "Stratifiés multicouches liés par diffusion thermique sous vide associant une toile hollandaise fine de haute précision à des armatures de renfort et de protection. Conçue pour une filtration de surface micrométrique et un contre-lavage performant.",
      secondaryCta: {
        label: "Voir toiles frittées",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Coupe transversale et surface de toile hollandaise frittée",
      },
      cardNote: "Liaison par diffusion à l'état solide",
      cardLink: {
        label: "Aperçu technique",
        to: "#overview",
      },
      specs: [
        { label: "Seuil de filtration", value: "1 µm – 200 µm" },
        { label: "Nuances standard", value: "AISI 316L / 304L" },
        { label: "Structure", value: "Cœur reps + armatures" },
        { label: "Formats", value: "Feuilles, disques et tubes" },
      ],
    },
    overview: {
      eyebrow: "Structure et ingénierie",
      heading: "Stratifiés monolithiques en toile reps frittée",
      paragraphs: [
        "La toile hollandaise frittée intègre un tissu métallique reps dense à des toiles de support grossières au sein d'un stratifié rigide unique grâce au frittage par diffusion sous vide poussé. Ce procédé métallurgique à l'état solide fusionne chaque croisement de fil à l'échelle atomique sans apport de brasure ni liant chimique.",
        "Si la toile hollandaise tissée standard offre des seuils micrométriques très précis, les fortes pertes de charge industrielles peuvent déplacer les fils libres et altérer la granulométrie de coupure. En liant par diffusion une couche protectrice en amont et des armatures rigides en aval, le stratifié conserve sa géométrie de pores même sous forts débits ou chocs de contre-lavage.",
        "Le composite rigide obtenu se comporte comme un média filtrant de surface incompressible. Le gâteau de filtration déposé en surface se détache aisément par rétro-lavage pulsé, bains d'ultrasons ou lavage chimique sans risque de délaminage.",
      ],
      keyPoints: [
        "La liaison par diffusion sous vide à l'état solide bloque définitivement les croisements de fils, éliminant tout relargage de particules.",
        "Haute tenue mécanique et rigidité structurelle pour résister aux fortes pressions différentielles et aux vibrations de procédé.",
        "Structure filtrante de surface régénérable par ultrasons, solvants chimiques, rétro-lavage et pyrolyse contrôlée.",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Photographie macro d'une toile hollandaise frittée en acier inoxydable",
      },
    },
    specTable: {
      heading: "Spécifications types de toiles hollandaises frittées",
      intro: "Configurations représentatives liées par diffusion sur les plages de filtration fines et intermédiaires. Études sur mesure disponibles.",
      columns: [
        { key: "mesh", label: "Toile hollandaise centrale" },
        { key: "rating", label: "Seuil nominal (µm)" },
        { key: "layers", label: "Empilement de couches" },
        { key: "thickness", label: "Épaisseur nominale" },
        { key: "alloys", label: "Alliage standard" },
      ],
      rows: [
        {
          mesh: "Reps uni 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 à 4 couches (cœur reps + tampon + armature)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Reps croisé 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 couches (protection + reps + tampon + support)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Reps croisé 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 à 5 couches (protection + reps + renfort)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Reps croisé 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 couches (protection + reps + tampon + double armature)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "Reps croisé 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 couches (protection + reps + triple support)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "Données représentatives selon les normes ASTM E2016 et ASTM E2814. Compositions spécifiques sur demande.",
    },
    features: {
      heading: "Avantages de performance",
      intro: "Conçue pour éliminer les déformations et défaillances des toiles non frittées sous fortes contraintes industrielles.",
      items: [
        {
          title: "Stabilité permanente des pores",
          text: "La diffusion à l'état solide bloque définitivement les fils de trame et de chaîne, empêchant tout élargissement de pore sous pression continue.",
        },
        {
          title: "Zéro relargage de matière",
          text: "L'absence de brasure et d'adhésif élimine tout risque de détachement de fragments métalliques ou d'impuretés chimiques dans le filtrat.",
        },
        {
          title: "Haute résistance à la pression",
          text: "Les toiles de renfort sous-jacentes répartissent les charges hydrauliques et évitent l'affaissement des plis ou les déchirures sous surpression.",
        },
        {
          title: "Régénération aisée",
          text: "La surface plane et rigide autorise des contre-lavages vigoureux et des cycles de nettoyage aux ultrasons avec récupération totale du débit.",
        },
      ],
    },
    related: {
      heading: "Solutions frittées complémentaires",
      intro: "Découvrez nos autres toiles métalliques frittées par diffusion et nos éléments sur mesure.",
      cards: [
        {
          title: "Toile métallique multicouche frittée",
          text: "Structures standard à 3 et 5 couches de mailles carrées pour la fluidisation, la filtration de gaz et la clarification de liquides.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "Disques et plaques en toiles métalliques multicouches frittées",
          },
          badge: "3 et 5 couches",
          linkLabel: "Voir toiles multicouches",
        },
        {
          title: "Toile frittée sur mesure",
          text: "Stratifiés spécifiques de 2 à 9+ couches associant toiles fines, tôles perforées et découpes laser de haute précision.",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "Stratifié de toile métallique frittée sur mesure",
          },
          badge: "Sur mesure",
          linkLabel: "Voir toiles sur mesure",
        },
        {
          title: "Gamme globale des toiles frittées",
          text: "Consultez l'ensemble de notre gamme de médias poreux métalliques frittés, guides de sélection et applications industrielles.",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "Éléments filtrants en toiles frittées",
          },
          badge: "Famille de produits",
          linkLabel: "Voir toutes les toiles frittées",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "Étude et fabrication",
      heading: "Demandez un devis de toile hollandaise frittée",
      lead: "Transmettez-nous votre seuil de coupure, la nuance d'inox, la perte de charge admissible et les dimensions requises pour une offre technique détaillée.",
      secondaryLabel: "Retour toiles frittées",
      secondaryTo: "/SinteredWireMeshPage",
      note: "Disponible en plaques standard, disques découpés laser, bandes refendues et cartouches soudées.",
    },
  },
  pt: {
    breadcrumbKey: "SinteredDutchWeaveMesh",
    relatedTopic: "sintered",
    hero: {
      categoryBadge: "Malha metálica sinterizada",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha holandesa sinterizada",
      lead: "Laminados multicamadas unidos por difusão a vácuo que combinam tecido metálico holandês de alta precisão com camadas de proteção e telas estruturais de suporte. Projetada para retenção superficial micrométrica, alta resistência à pressão e retrolavagem contínua.",
      secondaryCta: {
        label: "Ver malhas sinterizadas",
        to: "/SinteredWireMeshPage",
      },
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Corte transversal e superfície da malha holandesa sinterizada",
      },
      cardNote: "União por difusão em estado sólido",
      cardLink: {
        label: "Visão técnica",
        to: "#overview",
      },
      specs: [
        { label: "Grau de filtração", value: "1 µm – 200 µm" },
        { label: "Ligas padrão", value: "AISI 316L / 304L" },
        { label: "Construção", value: "Núcleo holandês + reforço" },
        { label: "Formatos", value: "Chapas, discos e tubos" },
      ],
    },
    overview: {
      eyebrow: "Engenharia e estrutura",
      heading: "Laminados monolíticos de malha holandesa sinterizada",
      paragraphs: [
        "A malha holandesa sinterizada combina tecido metálico holandês de trama fechada com camadas de malha quadrada grossa em um laminado rígido único através de sinterização por difusão sob alto vácuo. Esse processo no estado sólido funde atomicamente cada cruzamento de fios sem soldas de adição ou adesivos químicos, garantindo ausência de migração de fios e estabilidade dimensional absoluta dos poros.",
        "Enquanto os tecidos holandeses convencionais oferecem aberturas micrométricas exatas, pressões operacionais elevadas podem deslocar os fios soltos e descalibrar a retenção. Ao difundir camadas protetoras a montante e armaduras estruturais a jusante, o compósito retém sua calibração mesmo sob fluxo intenso e pulsos de retrolavagem.",
        "A matriz metálica rígida atua como meio incompressível de filtração de superfície. A torta de sólidos retida sobre a superfície exterior pode ser desprendida repetidamente por retrolavagem contracorrente, banhos de ultrassom ou limpeza química sem delaminação mecânica.",
      ],
      keyPoints: [
        "A sinterização por difusão sob vácuo funde os cruzamentos sem desprendimento de fibras ou deformação de poros.",
        "Elevada rigidez mecânica para suportar picos de pressão diferencial, ciclos de pulso reverso e vibrações industriais.",
        "Estrutura filtrante de superfície regenerável por ultrassom, solventes químicos, retrolavagem e queima térmica.",
      ],
      image: {
        src: SINTERED_DUTCH_IMAGE,
        alt: "Fotografia macro de malha holandesa sinterizada de aço inoxidável",
      },
    },
    specTable: {
      heading: "Especificações típicas de malha holandesa sinterizada",
      intro: "Configurações padrão unidas por difusão térmica em faixas finas e médias de filtração. Projetos sob medida disponíveis.",
      columns: [
        { key: "mesh", label: "Malha holandesa núcleo" },
        { key: "rating", label: "Retenção nominal (µm)" },
        { key: "layers", label: "Composição de camadas" },
        { key: "thickness", label: "Espessura nominal" },
        { key: "alloys", label: "Material padrão" },
      ],
      rows: [
        {
          mesh: "Holandês liso 80 × 400",
          rating: "25 – 30 µm",
          layers: "3 a 4 camadas (núcleo + amortecedor + suporte)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandês cruzado 165 × 1400",
          rating: "10 – 12 µm",
          layers: "4 camadas (proteção + núcleo + amortecedor + suporte)",
          thickness: "1.70 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandês cruzado 200 × 1400",
          rating: "8 – 10 µm",
          layers: "4 a 5 camadas (proteção + núcleo + reforço)",
          thickness: "1.70 mm – 2.00 mm",
          alloys: "AISI 316L / 304L",
        },
        {
          mesh: "Holandês cruzado 325 × 2300",
          rating: "2 – 3 µm",
          layers: "5 camadas (proteção + núcleo + amortecedor + reforço duplo)",
          thickness: "1.70 mm – 2.50 mm",
          alloys: "AISI 316L",
        },
        {
          mesh: "Holandês cruzado 400 × 2800",
          rating: "1 – 2 µm",
          layers: "5 camadas (proteção + núcleo + suporte triplo)",
          thickness: "1.70 mm – 2.80 mm",
          alloys: "AISI 316L",
        },
      ],
      caption: "Dados de referência conforme as normas ASTM E2016 e ASTM E2814. Configurações especiais sob encomenda.",
    },
    features: {
      heading: "Destaques de desempenho",
      intro: "Desenvolvida para superar os limites estruturais dos tecidos metálicos convencionais sob condições severas de processo.",
      items: [
        {
          title: "Estabilidade permanente de poros",
          text: "A difusão molecular fixa firmemente os fios de trama e urdume, impedindo aberturas ou alargamentos dos poros sob pressão contínua.",
        },
        {
          title: "Zero migração de meio filtrante",
          text: "Sem o uso de soldas metálicas ou ligantes poliméricos, não há liberação de fibras soltas ou contaminação química no filtrado.",
        },
        {
          title: "Resistência a alta pressão diferencial",
          text: "As camadas de suporte integradas distribuem o esforço hidrodinâmico, prevenindo colapso de pregas ou rompimento sob sobrepressão.",
        },
        {
          title: "Excelente capacidade de regeneração",
          text: "A superfície metálica lisa facilita a remoção da torta de filtração e permite retrolavagens periódicas com recuperação total do fluxo.",
        },
      ],
    },
    related: {
      heading: "Soluções complementares sinterizadas",
      intro: "Conheça outras configurações de malha metálica unida por difusão e capacidades de usinagem e conformação.",
      cards: [
        {
          title: "Malha multicamadas sinterizada",
          text: "Laminados padrão de 3 e 5 camadas de malha quadrada para leito fluidizado, purificação de gases e líquidos.",
          to: "/SinteredMultilayerMeshPage",
          image: {
            src: MULTILAYER_IMAGE,
            alt: "Discos e chapas de malha multicamadas sinterizada",
          },
          badge: "3 e 5 camadas",
          linkLabel: "Ver malhas multicamadas",
        },
        {
          title: "Malha sinterizada sob medida",
          text: "Composições personalizadas de 2 a 9+ camadas, integradas com chapas perfuradas e cortes especiais.",
          to: "/CustomSinteredMeshPage",
          image: {
            src: CUSTOM_SINTERED_IMAGE,
            alt: "Laminado de malha sinterizada sob medida",
          },
          badge: "Estruturas especiais",
          linkLabel: "Ver malha sob medida",
        },
        {
          title: "Gama completa de malhas sinterizadas",
          text: "Consulte nosso catálogo completo de meios metálicos porosos sinterizados, diretrizes técnicas e aplicações.",
          to: "/SinteredWireMeshPage",
          image: {
            src: SINTERED_PARENT_IMAGE,
            alt: "Elementos filtrantes sinterizados em aço inoxidável",
          },
          badge: "Família de produtos",
          linkLabel: "Ver todas as sinterizadas",
        },
      ],
    },
    cta: {
      id: "cta-sintered-dutch",
      eyebrow: "Engenharia e fornecimento",
      heading: "Solicite uma especificação de malha holandesa sinterizada",
      lead: "Informe sua retenção micrométrica, liga requerida, pressão diferencial de operação e dimensões para uma proposta técnica e comercial formal.",
      secondaryLabel: "Voltar para malhas sinterizadas",
      secondaryTo: "/SinteredWireMeshPage",
      note: "Disponível em chapas planas, discos cortados a laser, fitas fatiadas e cartuchos cilíndricos soldados.",
    },
  },
};
